import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { mediaHttp } from '@/lib/api/http-client';
import { parseResponse } from '@/lib/api/parse-response';
import type { CampaignRole } from '../model/performance.types';
import { MEDIA_CAMPAIGN_QUERY_KEYS } from './query-keys';
import {
  connectorCampaignsSchema,
  discoveredCampaignsSchema,
  projectCampaignsSchema,
} from './schemas';

export interface ProjectCampaign {
  id: string;
  name: string;
  /**
   * Where the campaign runs: its connector's, kept after a disconnect, or for a manual
   * campaign the one recorded from an upload or set by hand. It never says whether a
   * campaign is manual: `synced` does. Null when nothing recorded one.
   */
  platform: string | null;
  /** Still set after the connector is removed; `synced` says whether it feeds the campaign. */
  connectorId: string | null;
  /** Whether a live connector feeds the campaign; a campaign it does not feed is manual. */
  synced: boolean;
  /** Null for a campaign created from a delivery upload. */
  channel: string | null;
  status: string;
  servingStatus: string;
  flightStartDate: string | null;
  flightEndDate: string | null;
  /** Delivered spend within the pacing period. */
  spendToDate: number;
  /** Delivered vs expected spend, % (100 = on pace); null when not computable. */
  pacingPct: number | null;
  /**
   * Straight-line projection of spend by the end of the pacing period; null when there
   * is no run rate to extrapolate from (no period, or the period has not started yet).
   */
  projectedSpend: number | null;
  /** Budgets linked to this campaign; empty means there is nothing to pace against. */
  budgetIds: string[];
  currency: string | null;
  role: CampaignRole;
  counterpartCampaignId: string | null;
}

/**
 * One page, no follow-up request. Exported because a caller that reads absence from this
 * list ("this campaign is not in the project") is only entitled to that conclusion while
 * the page came back short - a full page may have left the campaign it was looking for on
 * a page nobody asked for.
 */
export const PROJECT_CAMPAIGNS_PAGE_SIZE = 200;

/** The campaigns feeding this project's dashboard. */
export const projectCampaignsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.campaigns(projectId),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: '/api/v1/media-campaigns',
        query: { projectId, pageSize: PROJECT_CAMPAIGNS_PAGE_SIZE },
        signal,
      });
      return parseResponse(projectCampaignsSchema, res).items;
    },
  });

export interface DiscoveredCampaign {
  id: string;
  connectorId: string;
  platform: string;
  externalCampaignId: string;
  externalCampaignName: string;
  firstSeenAt: string;
  lastSeenAt: string;
}

/**
 * Campaigns the sync found on connected accounts. Workspace-scoped on the server, so
 * these are suggestions for any project, not only this one.
 */
export const discoveredCampaignsQueryOptions = (projectId: string) =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.discoveredCampaigns(projectId),
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/media-data/projects/${projectId}/discovered-campaigns`,
        signal,
      });
      return parseResponse(discoveredCampaignsSchema, res);
    },
  });

export interface PlatformCampaignOption {
  externalId: string;
  name: string;
  status: string;
  currency?: string;
  startDate?: string;
  endDate?: string;
}

/** A connector's live campaigns, straight from the platform API. */
export const connectorCampaignsQueryOptions = (connectorId: string | null) =>
  queryOptions({
    queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.connectorCampaigns(connectorId ?? 'none'),
    // Nothing to ask until a connector is chosen.
    enabled: connectorId !== null,
    queryFn: async ({ signal }) => {
      const res = await mediaHttp.get({
        responseType: 'json',
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/media-connectors/${connectorId!}/campaigns`,
        signal,
      });
      return parseResponse(connectorCampaignsSchema, res).items;
    },
  });

export function useDismissDiscoveredCampaign(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await mediaHttp.post({
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/api/v1/media-data/discovered-campaigns/${id}/dismiss`,
      });
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: MEDIA_CAMPAIGN_QUERY_KEYS.discoveredCampaigns(projectId),
      }),
  });
}
